import { PlusIcon } from '@shopify/polaris-icons';
/**
 * VERIFY EMAIL PAGE
 *
 * Handles email verification from link in verification email.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  BlockStack,
  Text,
  Banner,
  Box,
  Button,
  Spinner,
} from '@shopify/polaris';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid verification link');
      setIsVerifying(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        const result = await response.json();

        if (!result.success) {
          if (result.code === 'TOKEN_EXPIRED') {
            setError('This verification link has expired. Please request a new one.');
          } else if (result.code === 'TOKEN_INVALID') {
            setError('This verification link is invalid or has already been used.');
          } else if (result.code === 'ALREADY_VERIFIED') {
            setSuccess(true); // Treat as success if already verified
          } else {
            setError(result.error || 'Failed to verify email');
          }
          return;
        }

        setSuccess(true);
      } catch {
        setError('An error occurred. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token]);

  if (isVerifying) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f6f6f7',
        gap: '16px',
      }}>
        <Spinner size="large" />
        <Text as="p" variant="bodyMd" tone="subdued">Verifying your email...</Text>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f6f6f7',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Logo/Brand */}
          <Box paddingBlockEnd="400">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                Whenly
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Delivery Date & Warehouse Management
              </Text>
            </BlockStack>
          </Box>

          {/* Result Card */}
          <Card>
            <BlockStack gap="400">
              {success ? (
                <>
                  <Banner tone="success">
                    Your email has been verified successfully!
                  </Banner>
                  <Text as="p" variant="bodyMd">
                    You can now sign in to your account.
                  </Text>
                  <Button
                    onClick={() => navigate('/login?success=email_verified')}
                    variant="primary"
                    fullWidth
                  >
                    Sign in
                  </Button>
                </>
              ) : (
                <>
                  <Banner tone="critical">
                    {error || 'Verification failed'}
                  </Banner>
                  <Text as="p" variant="bodyMd">
                    {error?.includes('expired')
                      ? 'Please sign in and request a new verification email.'
                      : 'The verification link may be invalid or has already been used.'}
                  </Text>
                  <BlockStack gap="200">
                    <Button
                      onClick={() => navigate('/login')}
                      variant="primary"
                      fullWidth
                    >
                      Sign in
                    </Button>
                    <Button icon={PlusIcon}
                      onClick={() => navigate('/register')}
                      fullWidth
                    >
                      Create new account
                    </Button>
                  </BlockStack>
                </>
              )}

              <Box paddingBlockStart="200">
                <Text as="p" variant="bodyMd" alignment="center">
                  <Link
                    to="/login"
                    style={{
                      color: '#2C6ECB',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Back to sign in
                  </Link>
                </Text>
              </Box>
            </BlockStack>
          </Card>

          {/* Help Text */}
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Need help? Contact{' '}
            <a href="mailto:support@whenly.app" style={{ color: '#2C6ECB' }}>
              support@whenly.app
            </a>
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}
